// ── M1: Auth Service ─────────────────────────────────────────
// Clerk session management, age verification, onboarding

import type { Profile, UserRole } from '@/types'
import type { AuthUser } from '@/types/auth'
import { supabase } from '@/lib/supabase'
import { isDemoMode_M12_4 } from '@/lib/constants'
import { getDemoUser_M11_3 } from './demo'

const AGE_VERIFIED_KEY = 'kinkup_age_verified'
const DEMO_SESSION_KEY = 'kinkup_demo_session'

// ── Age Verification ──

/** M1_3: Check localStorage for age verification flag */
export function checkAgeVerification_M1_3(): boolean {
  return localStorage.getItem(AGE_VERIFIED_KEY) === 'true'
}

/** M1_4: Store age verification in localStorage */
export function setAgeVerified_M1_4(): void {
  localStorage.setItem(AGE_VERIFIED_KEY, 'true')
}

// ── Onboarding ──

export interface OnboardingData {
  userId: string
  email: string
  role: UserRole
  displayName: string
}

/** M1_5: Check if user has completed onboarding (profile exists in Supabase) */
export async function checkOnboardingComplete_M1_5(userId: string): Promise<boolean> {
  if (isDemoMode_M12_4()) {
    return localStorage.getItem(DEMO_SESSION_KEY) !== null
  }

  const { data } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .single()

  return data !== null
}

/** M1_6: Create profile after onboarding wizard */
export async function completeOnboarding_M1_6(data: OnboardingData): Promise<Profile> {
  if (isDemoMode_M12_4()) {
    throw new Error('Onboarding not available in demo mode')
  }

  const { data: profile, error } = await supabase
    .from('profiles')
    .upsert({
      id: data.userId,
      email: data.email,
      role: data.role,
      display_name: data.displayName,
    } as any)
    .select()
    .returns<Profile[]>()
    .single()

  if (error) throw new Error(`Failed to create profile: ${error.message}`)
  return profile
}

// ── Demo Auth ──

export interface DemoCredentials {
  email: string
  password: string
}

/** M1_20: Sign in with demo account credentials */
export function demoSignIn_M1_20(creds: DemoCredentials): AuthUser | null {
  const user = getDemoUser_M11_3(creds.email, creds.password)
  if (user) {
    localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(user))
  }
  return user
}

/** M1_21: Clear demo session from localStorage */
export function demoSignOut_M1_21(): void {
  localStorage.removeItem(DEMO_SESSION_KEY)
}

/** Get current demo session (internal helper, not registered — transient) */
export function getDemoSession(): AuthUser | null {
  const raw = localStorage.getItem(DEMO_SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}
